import { BOOKING_ADDONS_PAGE_PREFERENCE_OPTIONS } from './app-contants';
import { isBookingPreferenceEnabled } from './appointment-addons';

export const AROMATHERAPY_SCENT_VALUES = ['DO', 'HEAL', 'LIVE', 'SLEEP'];
export const AROMATHERAPY_DEFAULT_SCENT = 'DO';

export function isAromatherapyScentValue(value) {
  return AROMATHERAPY_SCENT_VALUES.includes(String(value).toUpperCase());
}

/** Profile/API scent codes → booking yes/no (1 / 0) for add-ons UI. */
export function profileAromatherapyToBookingValue(value) {
  if (value === null || value === undefined) return value;
  if (isAromatherapyScentValue(value)) return 1;
  const n = Number(value);
  if (Number.isFinite(n) && (n === 0 || n === 1)) return n;
  return value;
}

/**
 * Booking yes/no (or stored scent) → PUT /user/preferences aromatherapy value.
 * Opt-in without an explicit scent choice is omitted from the profile payload.
 */
export function bookingAromatherapyToProfileValue(value) {
  if (value === null || value === undefined) return undefined;
  if (isAromatherapyScentValue(value)) return String(value).toUpperCase();
  const n = Number(value);
  if (Number.isFinite(n) && n === 0) return undefined;
  if (
    n === 1 ||
    value === 1 ||
    value === '1' ||
    String(value).toLowerCase() === 'yes'
  ) {
    return undefined;
  }
  return undefined;
}

/** True when the current booking session has aromatherapy set to yes. */
export function isBookingAromatherapyOptIn(bookingMassagePreferences) {
  let prefs = bookingMassagePreferences;
  if (prefs && typeof prefs.toJS === 'function') {
    prefs = prefs.toJS();
  }
  return isBookingPreferenceEnabled('aromatherapy', prefs);
}

function bookingValueMatchesOption(apiKey, optionValue, storedValue) {
  if (apiKey === 'aromatherapy') {
    const bookingVal = profileAromatherapyToBookingValue(storedValue);
    return optionValue.toString() === String(bookingVal);
  }
  return optionValue.toString() === storedValue.toString();
}

export const ADDON_PAGE_SESSION_PREF_KEYS = [
  'heat_therapy',
  'percussion_therapy',
  'aromatherapy',
  'deep_tissue',
];

const COMPLIMENTARY_BOOKING_PREF_KEYS = [
  'heat_therapy',
  'percussion_therapy',
  'aromatherapy',
];

/** Guest just created an account during the booking or registration flow. */
export function isNewBookingGuest() {
  if (typeof window === 'undefined') return false;
  try {
    if (window.sessionStorage.getItem('squeezeNewAccountCreated') === '1') {
      return true;
    }
    if (window.localStorage.getItem('isDirectBookingAppt') === 'true') {
      return true;
    }
    if (window.localStorage.getItem('isNewUser') === 'true') {
      return true;
    }
  } catch (e) {
    return false;
  }
  return false;
}

export function cloneBookingPreferenceOptions() {
  return BOOKING_ADDONS_PAGE_PREFERENCE_OPTIONS.map(opt => ({
    ...opt,
    values: opt.values.map(v => ({ ...v, selected: false })),
  }));
}

/** Build yes/no UI from `/user/preferences` only (no session overrides). */
export function buildAddonPageOptionsFromProfile(prefsArray, prefMapFn) {
  const prefMap = prefMapFn(prefsArray);
  return cloneBookingPreferenceOptions().map(o => {
    const copy = { ...o, values: o.values.map(v => ({ ...v })) };
    const existingSelection = getProfileValueForAddonPageOption(
      prefMap,
      o.apiKey,
    );
    copy.values.forEach(obj => {
      if (existingSelection !== null && existingSelection !== undefined) {
        // eslint-disable-next-line no-param-reassign
        obj.selected = bookingValueMatchesOption(
          o.apiKey,
          obj.value,
          existingSelection,
        );
      } else {
        // eslint-disable-next-line no-param-reassign
        obj.selected = false;
      }
    });
    return copy;
  });
}

/** Overlay in-session booking prefs onto options (no default-yes fallback). */
export function mergeBookingMapOntoPreferenceOptions(options, bookingMap) {
  if (!bookingMap || typeof bookingMap !== 'object') {
    return options;
  }
  return options.map(o => {
    if (
      !ADDON_PAGE_SESSION_PREF_KEYS.includes(o.apiKey) ||
      !Object.prototype.hasOwnProperty.call(bookingMap, o.apiKey)
    ) {
      return o;
    }
    const copy = { ...o, values: o.values.map(v => ({ ...v })) };
    const existingSelection = bookingMap[o.apiKey];
    copy.values.forEach(obj => {
      if (existingSelection !== null && existingSelection !== undefined) {
        // eslint-disable-next-line no-param-reassign
        obj.selected = bookingValueMatchesOption(
          o.apiKey,
          obj.value,
          existingSelection,
        );
      } else {
        // eslint-disable-next-line no-param-reassign
        obj.selected = false;
      }
    });
    return copy;
  });
}

/** Remove complimentary-pref keys from a booking session map (stale Redux). */
export function omitAddonPageKeysFromBookingMap(bookingMap) {
  const base =
    bookingMap && typeof bookingMap === 'object' ? { ...bookingMap } : {};
  ADDON_PAGE_SESSION_PREF_KEYS.forEach(key => {
    delete base[key];
  });
  return base;
}

/** True when the guest already has saved profile preferences (not brand new). */
export function userHasStoredProfilePreferences(prefsArray) {
  return Array.isArray(prefsArray) && prefsArray.length > 0;
}

/** True when profile has an explicit yes/no (or scent) for complimentary add-on prefs. */
export function profileHasAddonPagePreferenceSelections(prefsArray, prefMapFn) {
  if (!userHasStoredProfilePreferences(prefsArray) || !prefMapFn) {
    return false;
  }
  const prefMap = prefMapFn(prefsArray);
  return COMPLIMENTARY_BOOKING_PREF_KEYS.some(apiKey => {
    const v = getProfileValueForAddonPageOption(prefMap, apiKey);
    if (v === null || v === undefined) return false;
    // Saved scent (DO/HEAL/LIVE/SLEEP) is a preference choice, not add-on yes/no.
    if (apiKey === 'aromatherapy' && isAromatherapyScentValue(v)) {
      return false;
    }
    return true;
  });
}

function getProfileValueForAddonPageOption(prefMap, apiKey) {
  if (!prefMap || typeof prefMap !== 'object') {
    return null;
  }
  const entry = prefMap[apiKey];
  if (entry && entry.value !== null && entry.value !== undefined) {
    return entry.value;
  }
  if (apiKey === 'heat_therapy' && prefMap.heated_bed) {
    const v = prefMap.heated_bed.value;
    if (v !== null && v !== undefined) {
      return v === 1 || v === '1' ? 1 : 0;
    }
  }
  return null;
}

function isYesValue(value) {
  const t = String(value.title || '').toLowerCase();
  return t === 'yes';
}

/** New guests with no prior selection: default Heat, Percussion, and Aromatherapy to Yes. */
export function applyDefaultYesForNewBookingPreferences(options) {
  return options.map(o => {
    const copy = { ...o, values: o.values.map(v => ({ ...v })) };
    const hasSelection = copy.values.some(v => v.selected);
    if (hasSelection) return copy;
    const yesIdx = copy.values.findIndex(isYesValue);
    if (yesIdx < 0) return copy;
    copy.values.forEach((v, i) => {
      // eslint-disable-next-line no-param-reassign
      v.selected = i === yesIdx;
    });
    return copy;
  });
}

/**
 * Apply in-session booking prefs (Redux) onto cloned add-ons-page options.
 * When the map is empty, defaults apply only if `allowDefaultsWhenEmpty` (logged-out guests).
 */
export function mergeBookingPrefsIntoClonedOptions(
  bookingMap,
  { allowDefaultsWhenEmpty = false } = {},
) {
  const allowDefaults = allowDefaultsWhenEmpty || isNewBookingGuest();
  const base = cloneBookingPreferenceOptions();
  if (!bookingMap || typeof bookingMap !== 'object') {
    return allowDefaults ? applyDefaultYesForNewBookingPreferences(base) : base;
  }
  const keys = Object.keys(bookingMap);
  if (keys.length === 0) {
    return allowDefaults ? applyDefaultYesForNewBookingPreferences(base) : base;
  }
  const merged = base.map(o => {
    const copy = { ...o, values: o.values.map(v => ({ ...v })) };
    if (!Object.prototype.hasOwnProperty.call(bookingMap, o.apiKey)) {
      return copy;
    }
    const existingSelection = bookingMap[o.apiKey];
    copy.values.forEach(obj => {
      if (existingSelection !== null && existingSelection !== undefined) {
        // eslint-disable-next-line no-param-reassign
        obj.selected = bookingValueMatchesOption(
          o.apiKey,
          obj.value,
          existingSelection,
        );
      } else {
        // eslint-disable-next-line no-param-reassign
        obj.selected = false;
      }
    });
    return copy;
  });
  const anySelected = merged.some(o => o.values.some(v => v.selected));
  if (anySelected) {
    return merged;
  }
  return allowDefaults
    ? applyDefaultYesForNewBookingPreferences(merged)
    : merged;
}

/** Default yes params for heat, percussion, and aromatherapy (complimentary add-ons). */
export function getDefaultComplimentaryBookingPreferenceParams() {
  const options = applyDefaultYesForNewBookingPreferences(
    cloneBookingPreferenceOptions(),
  );
  const all = getPreferenceParamsFromOptions(options);
  return COMPLIMENTARY_BOOKING_PREF_KEYS.reduce((acc, key) => {
    if (Object.prototype.hasOwnProperty.call(all, key)) {
      acc[key] = all[key];
    }
    return acc;
  }, {});
}

export function isYesNoPreference(option) {
  if (!option.values || option.values.length !== 2) return false;
  const titles = option.values
    .map(v => (v.title || '').toLowerCase())
    .sort()
    .join(',');
  return titles === 'no,yes';
}

export function getPreferenceParamsFromOptions(preferenceOptions) {
  return preferenceOptions.reduce((acc, opt) => {
    const selected = opt.values.find(v => v.selected);
    if (selected !== undefined) {
      // eslint-disable-next-line no-param-reassign
      acc[opt.apiKey] = selected.value;
    }
    return acc;
  }, {});
}

/**
 * PUT /user/preferences from booking session prefs.
 * `deep_tissue` is booking-only (scheduling); pressure is stored as `customer_pressure`.
 */
export function normalizeBookingPrefsForProfileApi(prefs, options = {}) {
  if (!prefs || typeof prefs !== 'object') return {};
  const out = { ...prefs };
  delete out.deep_tissue;

  ['heat_therapy', 'heated_bed'].forEach(k => {
    if (!Object.prototype.hasOwnProperty.call(out, k)) return;
    const v = out[k];
    if (v === null || v === undefined) return;
    const n = Number(v);
    if (Number.isFinite(n) && (n === 0 || n === 1)) {
      out[k] = String(n);
    }
  });
  if (Object.prototype.hasOwnProperty.call(out, 'aromatherapy')) {
    const scent = bookingAromatherapyToProfileValue(out.aromatherapy);
    if (scent) {
      out.aromatherapy = scent;
    } else {
      delete out.aromatherapy;
    }
  }

  const { selectedMassageType } = options;
  if (selectedMassageType && typeof selectedMassageType === 'object') {
    const pressureName = selectedMassageType.name;
    if (pressureName) {
      out.customer_pressure = pressureName;
    }
    if (
      Object.prototype.hasOwnProperty.call(selectedMassageType, 'pregnancy')
    ) {
      out.bun_in_the_oven = selectedMassageType.pregnancy ? 1 : 0;
    }
    if (
      Object.prototype.hasOwnProperty.call(
        selectedMassageType,
        'firstTrimester',
      )
    ) {
      out.is_first_trimester = selectedMassageType.firstTrimester ? 1 : 0;
    }
  }

  return out;
}

/** Set yes/no selection for one add-ons-page preference option. */
export function setPreferenceYesNoInOptions(
  preferenceOptions,
  apiKey,
  wantYes,
) {
  return preferenceOptions.map(o => {
    if (o.apiKey !== apiKey) return o;
    const copy = { ...o, values: o.values.map(v => ({ ...v })) };
    const yesIdx = copy.values.findIndex(
      v => String(v.title || '').toLowerCase() === 'yes',
    );
    const noIdx = copy.values.findIndex(
      v => String(v.title || '').toLowerCase() === 'no',
    );
    copy.values.forEach((v, i) => {
      let fallbackIdx = noIdx;
      if (fallbackIdx < 0) {
        fallbackIdx = yesIdx === 0 ? 1 : 0;
      }
      // eslint-disable-next-line no-param-reassign
      v.selected = wantYes ? i === yesIdx : i === fallbackIdx;
    });
    return copy;
  });
}

export function applyFetchedPrefsToAddonPageOptions(
  prefsArray,
  prefMapFn,
  bookingMap,
) {
  const sessionMap =
    bookingMap && typeof bookingMap === 'object' ? bookingMap : {};
  const hasProfile = userHasStoredProfilePreferences(prefsArray);

  let options = hasProfile
    ? buildAddonPageOptionsFromProfile(prefsArray, prefMapFn)
    : cloneBookingPreferenceOptions();

  const shouldDefaultComplimentary =
    isNewBookingGuest() ||
    !profileHasAddonPagePreferenceSelections(prefsArray, prefMapFn);

  if (shouldDefaultComplimentary) {
    options = applyDefaultYesForNewBookingPreferences(options);
  }

  if (Object.keys(sessionMap).length > 0) {
    options = mergeBookingMapOntoPreferenceOptions(options, sessionMap);
  }

  return options;
}

/** Initial add-ons page preference UI from session, profile, or guest defaults. */
export function getInitialAddonPagePreferenceOptions({
  bookingMap = {},
  profilePreferences = null,
  isLoggedIn = false,
  profileFetchComplete = false,
  prefMapFn,
} = {}) {
  const sessionMap =
    bookingMap && typeof bookingMap === 'object' ? bookingMap : {};
  const hasSession = Object.keys(sessionMap).length > 0;
  const prefsArray = Array.isArray(profilePreferences)
    ? profilePreferences
    : [];
  const hasProfile = userHasStoredProfilePreferences(prefsArray) && prefMapFn;

  if (hasSession) {
    return mergeBookingPrefsIntoClonedOptions(sessionMap);
  }
  if (isNewBookingGuest()) {
    return applyDefaultYesForNewBookingPreferences(
      cloneBookingPreferenceOptions(),
    );
  }
  if (hasProfile) {
    return applyFetchedPrefsToAddonPageOptions(prefsArray, prefMapFn, {});
  }
  if (!isLoggedIn) {
    return mergeBookingPrefsIntoClonedOptions(
      {},
      { allowDefaultsWhenEmpty: true },
    );
  }
  // Logged-in new signup: empty profile after fetch → default complimentary add-ons to Yes.
  if (profileFetchComplete && prefMapFn) {
    return applyFetchedPrefsToAddonPageOptions([], prefMapFn, {});
  }
  return cloneBookingPreferenceOptions();
}
